import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  PLATFORM_ID,
  Renderer2,
  signal,
} from '@angular/core';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { BannerConfigService } from '../../services/banner.service';
import { catchError, map, of} from 'rxjs';
import { Billing, DisplayPlan, HomePagePopupData, PlanId } from '../../models/banner.models';
import { PricingPageUrl } from '../../enums/app.enums';
import { ProModalSessionService } from '../../services/pro-modal-session.service';
import { LocalstorageService } from '../../../core/services/essentials/localstorage.service';
import { environment } from '../../../../environments/environment';

const LAST_SHOWN_KEY = 'pricing_modal_last_shown';

@Component({
  selector: 'app-pro-modal',
  standalone: true,
  imports: [NgClass],
  templateUrl: './pro-modal.html',
  styleUrl: './pro-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProModal implements OnInit{
  private bannerService = inject(BannerConfigService)
  private renderer2 = inject(Renderer2)
  private platformId = inject(PLATFORM_ID)
  private proModalSession = inject(ProModalSessionService)
  private localstorageService = inject(LocalstorageService)
  environment = environment;
  displayIntervalHours = input(1)
  didnotSetOnLocalStorage = input(false)
  closePop = output<void>();
  protected pricingPageUrl = PricingPageUrl
  protected showProModal = signal(false)
  protected selectedbill = signal<Billing>('monthly')
  protected selectedPlanId = signal<PlanId>('standard')
  protected readonly billingLabel: Record<Billing, string> = {
    monthly: 'month',
    quarterly: 'quarter',
    yearly: 'year',
  };

  private readonly billingId : Record<Billing, number> = {
    monthly: 1,
    quarterly: 3,
    yearly: 12,
  }

  private readonly planId: Record<string, number> = {
    basic: 2,
    standard: 3,
    premium: 4,
  }

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    
    effect((onCleanup) => {
      const isOpen = this.showProModal();
      this.renderer2.setStyle(document.body, 'overflow', isOpen ? 'hidden' : '');

      onCleanup(() => {
        this.renderer2.setStyle(document.body, 'overflow', '');
      });
    });
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const shouldShow =  
      this.proModalSession.consumeProModalShowForSession() &&
      this.hasIntervalPassed()

    if (shouldShow || this.didnotSetOnLocalStorage()) {
      this.showProModal.set(true);
      this.saveLastShownTime();
    }
  }

  protected onBillingSelect(type: Billing): void {
    this.selectedbill.set(type);
    this.ensureSelectedPlan();
  }

  protected readonly popupData = toSignal(
    this.bannerService.getHomePagePopupData().pipe(
      map((data) => data.homePagePricingPop as HomePagePopupData),
      catchError(() => of(null))
    ),
    {
      initialValue: null,
    }
  );

  protected readonly selectedPlans = computed<DisplayPlan[]>(() => {
    const data = this.popupData();

    if (!data) {
      return [];
    }

    const option = data.packageOptions.find(
      (item) => item.key === this.selectedbill()
    );

    if (!option) {
      return [];
    }

    return option.plans
      .map((pricePlan) => {
        const benefitConfig = data.benefits.find(
          (benefitPlan) => benefitPlan.id === pricePlan.id
        );

        if (!benefitConfig) {
          return null;
        }

        return {
          ...pricePlan,
          ...benefitConfig,
        };
      })
      .filter((plan): plan is DisplayPlan => plan !== null);
  });

  protected readonly selectedPlan = computed<DisplayPlan | null>(() => {
    const selectedId = this.selectedPlanId();

    return (
      this.selectedPlans().find((plan) => plan.id === selectedId) ??
      this.selectedPlans()[0] ??
      null
    );
  });

  protected onPlanSelect(plan: DisplayPlan): void {
    this.selectedPlanId.set(plan.id);
  }

  protected readonly isPaymentRedirecting = signal(false)

  protected redirectToPayment(): void {
    if (!isPlatformBrowser(this.platformId) || this.isPaymentRedirecting()) {
      return;
    }

    const selectedPlan = this.selectedPlan();

    if (!selectedPlan) {
      return;
    }

    const pricingPaymentUrl =
      'https://mybdjobs.bdjobs.com/mybdjobs/candidate_package/candidate_package_payment.asp';

    const pid = this.planId[selectedPlan.id];
    const duration = this.billingId[this.selectedbill()];
    const url = `${pricingPaymentUrl}?pid=${pid}&d=${duration}&t=no`;

    this.isPaymentRedirecting.set(true);

    setTimeout(() => {
      window.location.assign(url);
    }, 0);
  }

  private ensureSelectedPlan(): void {
    const plans = this.selectedPlans();

    if (!plans.length) {
      return;
    }

    const currentId = this.selectedPlanId();

    if (plans.some((plan) => plan.id === currentId)) {
      return;
    }

    this.selectedPlanId.set('standard');
  }

  private hasIntervalPassed(): boolean {
    const lastShown = this.localstorageService.getItem(LAST_SHOWN_KEY);
    if (!lastShown) {
      return true; // Never shown before → show it
    }

    const elapsedHours = (Date.now() - Number(lastShown)) / (1000 * 60 * 60);
    console.log("hours", elapsedHours, this.displayIntervalHours(), elapsedHours >= this.displayIntervalHours())
    return elapsedHours >= this.displayIntervalHours();
  }

  private saveLastShownTime(): void {
    if(this.didnotSetOnLocalStorage()) return;
    this.localstorageService.setItem(LAST_SHOWN_KEY, Date.now().toString());
  }

  closePricingPop():void{
    this.showProModal.set(false);
    this.closePop.emit();
  }
}
